import React from "react";
import { formatNumber } from "@/lib/utils";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle } from "@/components/shared/print";
import { legacyBarcodeLabel, legacyTestLabel } from "@/components/quality/qc-form/LegacyMeasurements";
import {
  QcPrintConclusion,
  QcPrintCriteriaTable,
  QcPrintDefects,
  QcPrintInfoGrid,
  QcPrintLegacy,
  QcPrintSamplingRow,
  QcPrintSignatures,
  checklistOf,
} from "./qc-print-blocks";

interface QCFinalPrintTemplateProps {
  data?: any;
  config?: any;
}

/** BM-QC-03 — Biên bản kiểm tra thành phẩm (1 trang A4 khi ≤ 15 chỉ tiêu) */
export default function QCFinalPrintTemplate({ data = {}, config }: QCFinalPrintTemplateProps) {
  const docId =
    data?.finalQcId ||
    data?.code ||
    (data?.id?.length > 20 ? `FQC-${data.id.slice(0, 8).toUpperCase()}` : data?.id) ||
    "";
  const po = data.productionOrder || {};
  const lotSize = Number(data.lotSize ?? data.qtyChecked ?? 0);
  const isLegacy = !data.inspectionMode;
  const printingColor = data.printingIpqcSummary || null;

  return (
    <div
      className="p-[8mm] text-black leading-tight text-[10pt] bg-white w-full max-w-[210mm] mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <ISOPrintHeader config={config} />

      <PrintDocumentTitle
        title="BIÊN BẢN KIỂM TRA THÀNH PHẨM"
        docNo={docId}
        docNoLabel="Số phiếu"
        extraMeta="BM-QC-03"
        date={data.checkTime || data.createdAt || new Date()}
        dateLabel="Ngày kiểm"
      />

      <QcPrintInfoGrid
        items={[
          ["Lệnh SX", po.orderId || data.productionOrderId],
          ["Sản phẩm", data.productName || po.productName || po.product?.productName],
          ["Khách hàng", po.customer?.name || data.customer?.name || data.customerName],
          ["Đơn hàng", po.salesOrder?.orderId || data.order?.orderId],
          ["Loại SP", data.productType?.code],
          ["Mẫu chuẩn", data.referenceSample],
          // Số lô, quy cách thùng thuộc Phiếu đóng gói (Sản xuất), không in trên biên bản KCS
        ]}
      />

      <QcPrintSamplingRow
        lotSize={lotSize}
        mode={data.inspectionMode}
        level={data.aqlLevel}
        aqlMajor={data.aqlStandard}
        aqlMinor={data.aqlMinor}
        sampleSize={data.sampleSize}
        acMajor={data.acValue}
        reMajor={data.reValue}
        acMinor={data.acMinor}
        reMinor={data.reMinor}
      />

      <QcPrintCriteriaTable items={checklistOf(data.checklist)} />

      {isLegacy && (
        <QcPrintLegacy
          rows={[
            [
              "D×R×C",
              data.actualLength || data.actualWidth || data.actualHeight
                ? `${data.actualLength ?? "—"}×${data.actualWidth ?? "—"}×${data.actualHeight ?? "—"} mm`
                : null,
            ],
            ["Độ ẩm", data.moisture !== null && data.moisture !== undefined ? `${data.moisture} %` : null],
            ["Mã vạch", legacyBarcodeLabel(data.barcodeGrade)],
            ["Dựng hộp", legacyTestLabel(data.fittingTestResult)],
            ["Thả rơi", legacyTestLabel(data.dropTestResult)],
          ]}
        />
      )}

      {printingColor && (
        <div className="mb-2 text-[8.5pt]">
          <b>Màu in (IPQC):</b> ΔE = {printingColor.deltaE ?? "—"} (L*{printingColor.labActL ?? "—"} a*{printingColor.labActA ?? "—"} b*
          {printingColor.labActB ?? "—"})
        </div>
      )}

      <QcPrintDefects items={data.defectDetails || data.defectBreakdown} />

      <QcPrintConclusion
        status={data.status}
        counts={{ critical: data.criticalDefects || 0, major: data.majorDefects || 0, minor: data.minorDefects || 0 }}
        reMajor={data.reValue}
        reMinor={data.reMinor}
        overrideReason={data.overrideReason}
        notes={data.notes}
        extra={
          <>
            SL nhập kho <b>{formatNumber(Number(data.qtyPassed || 0))}</b> · SL loại <b>{formatNumber(Number(data.qtyFailed || 0))}</b>
          </>
        }
      />

      <QcPrintSignatures
        columns={[
          { title: "Người kiểm", hint: "(Ký, ghi rõ họ tên)", name: data.inspector?.fullName || data.inspectorName },
          { title: "Trưởng QC", hint: "(Ký duyệt)" },
          { title: "Duyệt xuất", hint: "(Ký duyệt xuất xưởng)" },
        ]}
      />
    </div>
  );
}
